import { Sidebar } from 'flowbite-react';
import { Text, Spinner, IconLoader } from '@SBGSports/catapult-core';
import { TfActivity, useGetActivities } from 'api';
import { useTranslations } from 'i18n';
import { ActivitiesSortEnum, Activity } from 'oas';
import React, { useCallback, useMemo, useState } from 'react';
import { getEndOfDay, getStartOfDay } from '../utils';
import { stripTagGroupSuffix } from 'shared/utils/tag-filter-keys';
import { useLoadMoreObserver } from 'shared';
import { useActivityListHeightObserver } from './activity-side-menu-custom-hook';
import { ActivitySideMenuFilters } from './activity-side-menu-filters';
import styles from './activity-side-menu.module.css';
import { useSideMenuFilterState } from './use-side-menu-filter-state';
import Highcharts from 'highcharts/highstock';
import { ActivityListItem } from './components/activity-list-item';
import { useSingleActivityLogic } from './hooks/use-single-activity-logic';
import { useMultiActivityLogic } from './hooks/use-multi-activity-logic';
import { useRovingTabIndex } from '../../activity-editor/hooks/use-roving-tab-index';
import { BaseReportType } from 'reporting/types';

type ActivitySideMenuMode = 'single' | 'multi';

interface ActivitySideMenuProps {
    mode: ActivitySideMenuMode;
    pageSize: number;
    customContent?: React.ReactElement;
    defaultExpanded?: boolean;
    customFooter?: JSX.Element;
    reportType: BaseReportType;
    teamFilters?: string[];
    applyReportTypeDefaults?: boolean;

    // Single mode props
    viewPath?: string;
    selectedActivity?: string;
    setSelectedActivity?: (id: string) => void;
    disabledExpandCollapse?: boolean;

    // Multi mode props
    selectedActivities?: TfActivity[];
    setSelectedActivities?: (activities: TfActivity[]) => void;
    activitiesList?: TfActivity[];
    setActivitiesList?: (activities: TfActivity[]) => void;
    weeksToPreselect?: number;
    setActivitiesLoading?: (l: boolean) => void;
}

const noOp = () => {
    // used as default callback for unused mode
};

export const ActivitySideMenu: React.FC<React.PropsWithChildren<ActivitySideMenuProps>> = (props) => {
    const {
        mode,
        customContent,
        pageSize,
        defaultExpanded = true,
        customFooter,
        reportType,
        teamFilters,
        applyReportTypeDefaults = false,
    } = props;

    const { __ } = useTranslations();
    const [collapsed, setCollapsed] = useState(!defaultExpanded);

    // Filter state - unified for both modes, synced to URL
    const { textFilter, setTextFilter, dateFilters, setDateFilters, tagFilters, setTagFilters } =
        useSideMenuFilterState({ applyReportTypeDefaults });

    // Extract mode-specific props
    const {
        viewPath = '',
        selectedActivity = '',
        setSelectedActivity = noOp,
        disabledExpandCollapse = false,
        selectedActivities = [],
        setSelectedActivities = noOp,
        activitiesList = [],
        setActivitiesList = noOp,
        weeksToPreselect,
        setActivitiesLoading,
    } = props;

    // Setup activity query
    const startOfDay = getStartOfDay(dateFilters.from);
    const endOfDay = getEndOfDay(dateFilters.to);

    // The API filters by name, so strip the ~groupKey suffix and de-duplicate.
    // Duplicates arise when the same tag name is selected from multiple groups.
    const tagFilterNames = useMemo(() => {
        const names = tagFilters.map(stripTagGroupSuffix);
        return [...new Set(names)].sort();
    }, [tagFilters]);

    const infiniteQuery = useGetActivities(
        pageSize,
        ActivitiesSortEnum.StartTimeDesc,
        0,
        mode === 'multi' ? ['activity_athletes'] : undefined,
        startOfDay ? startOfDay.getTime() / 1000 : undefined,
        endOfDay ? endOfDay.getTime() / 1000 : undefined,
        textFilter,
        {
            getNextPageParam: (lastPage: Activity[], pages: Activity[][]) => {
                return lastPage.length >= pageSize ? pages.length + 1 : undefined;
            },
        },
        tagFilterNames,
        teamFilters,
    );

    const flattenedResults = useMemo(() => {
        return infiniteQuery.data?.pages.flatMap((page) => page);
    }, [infiniteQuery.data?.pages]);

    // Single mode logic
    const singleLogic = useSingleActivityLogic({
        viewPath,
        selectedActivity,
        setSelectedActivity,
        firstActivityId: mode === 'single' ? flattenedResults?.[0]?.id : undefined,
    });

    // Multi mode logic
    const multiLogic = useMultiActivityLogic({
        selectedActivities,
        setSelectedActivities,
        activitiesList,
        setActivitiesList,
        flattenedResults: mode === 'multi' ? flattenedResults : undefined,
        weeksToPreselect,
        reportType,
        setActivitiesLoading,
        isLoading: infiniteQuery.isLoading,
        dateFilters,
    });

    const { activityListRef, sideMenuFooterRef } = useActivityListHeightObserver();
    const { observerElem } = useLoadMoreObserver({
        hasNextPage: infiniteQuery.hasNextPage,
        fetchNextPage: infiniteQuery.fetchNextPage,
        root: activityListRef.current,
    });

    const {
        panelRef: rovingPanelRef,
        selectedIndex: activeIndex,
        setSelectedIndex: setActiveIndex,
    } = useRovingTabIndex(flattenedResults?.length ?? 0, noOp, 'vertical');

    // Merge the roving tabIndex ref with the height observer ref onto the same DOM node
    const mergedListRef = useCallback(
        (node: HTMLDivElement | null) => {
            rovingPanelRef.current = node;
            (activityListRef as { current: HTMLDivElement | null }).current = node;
        },
        [rovingPanelRef, activityListRef],
    );

    // Early returns for shared link or status report
    const shouldRenderChildrenOnly =
        mode === 'single' ? singleLogic?.shouldRenderChildrenOnly : multiLogic?.shouldRenderChildrenOnly;

    if (shouldRenderChildrenOnly) {
        return <>{props.children}</>;
    }

    const isLoading = infiniteQuery.isLoading || (mode === 'multi' && multiLogic && !multiLogic.indicesLoaded);

    return (
        <div
            className={styles['side-bar-container']}
            onTransitionEnd={() => {
                Highcharts.charts.forEach((c) => c?.reflow());
            }}
        >
            <Sidebar
                aria-label="Activity side menu"
                collapsed={collapsed}
                className={styles['sidebar']}
                theme={{
                    root: {
                        base: styles['sidebar-root'],
                        collapsed: {
                            on: styles['sidebar-collapsed'],
                            off: styles['sidebar-expanded'],
                        },
                        inner: styles['sidebar-inner'],
                    },
                }}
            >
                {!disabledExpandCollapse && (
                    <button
                        type="button"
                        onClick={() => setCollapsed(!collapsed)}
                        className={styles['collapse-button']}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <IconLoader
                            iconName={collapsed ? 'IconChevronRight' : 'IconChevronLeft'}
                            size="sm"
                            className={styles['collapse-icon']}
                        />
                    </button>
                )}

                <div className={styles['sidebar-content']}>
                    <div className={styles['header-row']}>
                        <div data-testid={'activity-menu-title'} className={styles['activity-menu-title']}>
                            <Text variant="heading-medium">{__('misc.activities')}</Text>
                            <Text className="text-system-on-surface-variant" variant="heading-small">{` (${
                                flattenedResults?.length ?? 0
                            })`}</Text>
                        </div>
                    </div>

                    <div role="search" aria-label="Activity filters" className={styles['filter-column']}>
                        {customContent}
                        <ActivitySideMenuFilters
                            textFilter={mode === 'multi' ? textFilter : undefined}
                            setTextFilter={setTextFilter}
                            selectedDateRange={dateFilters}
                            setDateRange={setDateFilters}
                            tagFilters={tagFilters}
                            setTagFilters={setTagFilters}
                            dataLoading={infiniteQuery.isLoading || infiniteQuery.isFetchingNextPage}
                            dateRangePlaceholder={mode === 'multi' ? multiLogic?.dateRangePlaceholder : undefined}
                            reportType={reportType as BaseReportType}
                        />
                    </div>

                    <div
                        {...(mode === 'multi' ? { role: 'listbox' as const, 'aria-multiselectable': true } : {})}
                        aria-label={__('misc.activities')}
                        className={styles['activity-list']}
                        ref={mergedListRef}
                    >
                        {isLoading ? (
                            <span className={styles['centered-span']}>
                                <Spinner testId={'activity-search-spinner'} />
                            </span>
                        ) : (
                            flattenedResults?.map((activity: TfActivity, i: number) => {
                                const isSelected =
                                    mode === 'single'
                                        ? singleLogic?.isSelected(activity.id) ?? false
                                        : multiLogic?.isSelected(activity) ?? false;

                                if (mode === 'single' && singleLogic) {
                                    return (
                                        <ActivityListItem
                                            key={activity.id}
                                            mode="single"
                                            activity={activity}
                                            isSelected={isSelected}
                                            onSelect={(activityId) => {
                                                setActiveIndex(i);
                                                singleLogic.handleSelection(activityId);
                                            }}
                                            getPath={singleLogic.getPath}
                                            isActive={i === activeIndex}
                                        />
                                    );
                                } else if (mode === 'multi' && multiLogic) {
                                    return (
                                        <ActivityListItem
                                            key={activity.id}
                                            mode="multi"
                                            activity={activity}
                                            isSelected={isSelected}
                                            onSelect={(act, idx, isShift, isMeta) => {
                                                setActiveIndex(i);
                                                multiLogic.handleSelection(act, idx, isShift, isMeta);
                                            }}
                                            index={i}
                                            isActive={i === activeIndex}
                                        />
                                    );
                                }
                                return null;
                            })
                        )}
                        {(infiniteQuery.isFetchingNextPage || infiniteQuery.hasNextPage) && (
                            <span className={styles['centered-span']}>
                                <Spinner ref={observerElem} />
                            </span>
                        )}
                    </div>

                    {customFooter && (
                        <div ref={sideMenuFooterRef} className={styles['sidebar-footer']}>
                            {customFooter}
                        </div>
                    )}
                </div>
            </Sidebar>
            {props.children}
        </div>
    );
};
